import { useState } from 'react';
import { Search, FileText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useQuizTemplates } from '@/hooks/useQuizTemplates';
import { QuizTemplate } from '@/types/quiz';

interface TemplateSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectTemplate: (template: QuizTemplate) => void;
  onStartFromScratch: () => void;
  brandId?: string;
}

const TEMPLATE_CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'lead-generation', label: 'Lead Generation' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'personality', label: 'Personality' },
  { value: 'feedback', label: 'Feedback & Survey' },
  { value: 'product-recommendation', label: 'Product Recommendation' },
  { value: 'trivia', label: 'Trivia & Fun' },
  { value: 'custom', label: 'Custom' },
];

export const TemplateSelector = ({
  open,
  onOpenChange,
  onSelectTemplate,
  onStartFromScratch,
  brandId,
}: TemplateSelectorProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const { data: templates, isLoading } = useQuizTemplates(
    brandId,
    selectedCategory === 'all' ? undefined : selectedCategory
  );

  const filteredTemplates = templates?.filter((template) =>
    template.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogTitleWithBug
          popupName="Template Selector"
          popupContext="/quiz-builder"
          isOpen={open}
        >
          Create Quiz
        </DialogTitleWithBug>

        <div className="space-y-4">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TEMPLATE_CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="border rounded-lg p-4 bg-muted/50">
            <Button
              variant="outline"
              className="w-full h-auto py-6"
              onClick={() => {
                onStartFromScratch();
                onOpenChange(false);
              }}
            >
              <div className="text-center">
                <FileText className="h-8 w-8 mx-auto mb-2" />
                <div className="font-semibold">Start from Scratch</div>
                <div className="text-sm text-muted-foreground">
                  Create a custom quiz from the beginning
                </div>
              </div>
            </Button>
          </div>

          <div>
            <h3 className="font-semibold mb-3">Or choose a template:</h3>
            {isLoading ? (
              <div className="text-center py-8">Loading templates...</div>
            ) : filteredTemplates && filteredTemplates.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[400px] overflow-y-auto">
                {filteredTemplates.map((template) => (
                  <Card
                    key={template.id}
                    className="cursor-pointer hover:bg-accent transition-colors"
                    onClick={() => {
                      onSelectTemplate(template);
                      onOpenChange(false);
                    }}
                  >
                    {template.thumbnail_url && (
                      <div className="h-32 overflow-hidden rounded-t-lg">
                        <img
                          src={template.thumbnail_url}
                          alt={template.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <CardTitle className="text-base">{template.name}</CardTitle>
                        {template.is_global && (
                          <Badge variant="secondary" className="text-xs">
                            Global
                          </Badge>
                        )}
                      </div>
                      {template.description && (
                        <CardDescription className="text-sm line-clamp-2">
                          {template.description}
                        </CardDescription>
                      )}
                      {template.category && (
                        <Badge variant="outline" className="w-fit mt-2">
                          {template.category}
                        </Badge>
                      )}
                    </CardHeader>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No templates found. Try a different search or category.
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
